import { useState } from "react"
import { NavLink, useLocation } from "react-router-dom"

import {
	Box,
	Drawer as MuiDrawer,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	SvgIcon,
	Typography,
	Theme,
	CSSObject,
	styled,
	IconButton,
	ListItemIcon,
	Tooltip,
} from "@mui/material"
import { ArrowLeftSLineIcon, PriceTag3LineIcon, MenuLineIcon } from "@equipmentshare/ds2-icons"
import { ListAlt } from "@mui/icons-material"

import { ROUTE_PATHS } from "@app/const/RoutePaths"
import { UserProfile } from "./UserProfile"
import { useFlags } from "@equipmentshare/feature-flags"

type RouteItem = {
	name: string
	path: string
	icon?: React.ElementType
}

const DRAWER_WIDTH = "270px"
const COLLAPSED_WIDTH = "72px"

const openedMixin = (theme: Theme): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
})

const closedMixin = (theme: Theme): CSSObject => ({
	width: COLLAPSED_WIDTH,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
})

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
	width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
	}),
	flexShrink: 0,
	whiteSpace: "nowrap",
	"& .MuiDrawer-paper": {
		boxSizing: "border-box",
		borderRight: "none",
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		backgroundColor: theme.palette.grey[50],
		...(open ? openedMixin(theme) : closedMixin(theme)),
	},
}))

export const Sidebar: React.FC = () => {
	const [open, setOpen] = useState(true)
	const location = useLocation()
	const { enableBulkLabelGeneratorLastCreated } = useFlags()

	const navItems: RouteItem[] = Object.values(ROUTE_PATHS)
		.filter((item) => item !== ROUTE_PATHS.HOME)
		.filter((item) => {
			// Hide Last Created page if feature flag is disabled
			if (item === ROUTE_PATHS.LAST_CREATED && !enableBulkLabelGeneratorLastCreated) {
				return false
			}
			return true
		})
		.map((item) => {
			let icon: React.ElementType | undefined
			switch (item) {
				case ROUTE_PATHS.LABEL_GENERATOR:
					icon = PriceTag3LineIcon
					break
				case ROUTE_PATHS.LAST_CREATED:
					icon = ListAlt
					break
				default:
					icon = undefined
			}
			return { name: item.name, path: item.path, icon }
		})

	const handleToggle = () => setOpen((prevOpen) => !prevOpen)

	return (
		<Drawer variant="permanent" open={open}>
			<Box>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent={open ? "space-between" : "center"}
					sx={{ p: 2, paddingBottom: "30px" }}
				>
					{open && (
						<Typography variant="h6" fontWeight="bold" noWrap>
							Bulk Label
						</Typography>
					)}

					<IconButton
						onClick={handleToggle}
						aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
						sx={{
							borderRadius: 1,
							minWidth: 50,
							minHeight: 44,
						}}
					>
						{open ? <SvgIcon component={ArrowLeftSLineIcon} /> : <SvgIcon component={MenuLineIcon} />}
					</IconButton>
				</Stack>
				<List>
					{navItems.map((item) => {
						const isActive = location.pathname === item.path
						const icon = item.icon ?? PriceTag3LineIcon

						const buttonStyles = {
							px: open ? 2 : 1,
							py: 1,
							minHeight: 44,
							"&.active": (theme: any) => ({
								backgroundColor: theme.palette.action.selected,
								"& .MuiListItemText-primary": {
									color: theme.palette.primary.main,
									fontWeight: 700,
								},
							}),
							".MuiListItemText-primary": (theme: any) => ({
								color: theme.palette.grey[700],
								fontWeight: 600,
							}),
							justifyContent: open ? "flex-start" : "center",
							borderRadius: 1,
							mx: 1,
						}

						const content = (
							<ListItemButton
								key={item.path}
								component={NavLink}
								to={item.path}
								className={isActive ? "active" : undefined}
								sx={buttonStyles}
							>
								<ListItemIcon
									sx={{
										minWidth: 0,
										mr: open ? 1.5 : 0,
										justifyContent: "center",
									}}
								>
									<SvgIcon component={icon} color={isActive ? "primary" : "inherit"} />
								</ListItemIcon>
								{open && <ListItemText primary={item.name} />}
							</ListItemButton>
						)

						return open ? (
							content
						) : (
							<Tooltip key={item.path} title={item.name} placement="right">
								<Box component="span">{content}</Box>
							</Tooltip>
						)
					})}
				</List>
			</Box>
			<Box
				sx={{
					p: 2,
					bgcolor: (theme) => theme.palette.grey[200],
				}}
			>
				<UserProfile compact={!open} />
			</Box>
		</Drawer>
	)
}
export default Sidebar
